import { createReducer } from '@reduxjs/toolkit';

import { MessengerTranslatedLanguage } from 'components/settings/components/sections/dixaMessenger/views/settings/components/generalSettings/languageSettings/languages';

import { messengerSettingsActions as actions } from './messengerSettings.actions';
import {
  ElevioConnectedState,
  ElevioIntegrationState,
  ElevioMessengerDisplayType,
  TranslatedString,
} from './messengerSettings.types';

const makeTranslatedString = (
  translatedMap: Partial<Record<MessengerTranslatedLanguage, string>> = {},
): TranslatedString => ({
  translatedMap,
  maxLength: 50,
});

const mkConnectedState = (
  companyId: string,
  enabled: boolean,
  findArticles: TranslatedString = makeTranslatedString(),
  searchBarPlaceholder: TranslatedString = makeTranslatedString(),
  displayType: ElevioMessengerDisplayType = 'display-button-for-access-to-articles',
): ElevioConnectedState => ({
  type: 'connected',
  companyId,
  enabled,
  displayType,
  translations: {
    findArticles,
    searchBarPlaceholder,
  },
});

export type MessengerSettingsReducerState = {
  selectedLanguage: MessengerTranslatedLanguage;
  elevioIntegration: ElevioIntegrationState;
  pristineElevioIntegration: ElevioIntegrationState;
};

const initialState: MessengerSettingsReducerState = {
  selectedLanguage: 'en',
  elevioIntegration: {
    type: 'not-connected',
  },
  pristineElevioIntegration: {
    type: 'not-connected',
  },
};

export const messengerSettingsReducer = createReducer(
  initialState,
  (builder) => {
    builder
      .addCase(actions.initialize, (state, action) => {
        const settings = action.payload;
        const elevioIntegration: ElevioIntegrationState =
          settings.integrations.elevio !== undefined
            ? mkConnectedState(
                settings.integrations.elevio.companyId,
                settings.integrations.elevio.enabled,
                makeTranslatedString(settings.general.findArticles),
                makeTranslatedString(
                  settings.integrations.elevio.searchBarPlaceholder,
                ),
                settings.integrations.elevio.displayType,
              )
            : {
                type: 'not-connected',
              };
        state.pristineElevioIntegration = elevioIntegration;
        state.elevioIntegration = elevioIntegration;
      })
      .addCase(actions.reset, (state) => {
        return {
          ...initialState,
          pristineElevioIntegration: state.pristineElevioIntegration,
          elevioIntegration: state.pristineElevioIntegration,
        };
      })
      .addCase(actions.setElevioCompanyId, (state, action) => {
        if (state.elevioIntegration.type === 'not-connected') {
          state.elevioIntegration = mkConnectedState(action.payload, true);
        } else {
          state.elevioIntegration.companyId = action.payload;
        }
      })
      .addCase(actions.toggleElevioIntegration, (state) => {
        if (state.elevioIntegration.type === 'connected') {
          state.elevioIntegration.enabled = !state.elevioIntegration.enabled;
        }
      })
      .addCase(actions.updateDisplayType, (state, action) => {
        if (state.elevioIntegration.type !== 'not-connected') {
          state.elevioIntegration.displayType = action.payload;
        }
      })
      .addCase(actions.changeSelectedLanguage, (state, action) => {
        state.selectedLanguage = action.payload;
      })
      .addCase(actions.updateTranslatedString, (state, action) => {
        if (state.elevioIntegration.type !== 'not-connected') {
          const { translatable, language, value } = action.payload;
          state.elevioIntegration.translations[translatable].translatedMap[
            language
          ] = value;
        }
      });
  },
);
